"use client";

import type { SubcategoryListingRule } from "@/lib/subcategoryRules";
import { SIO_BANNER_POSITIVE } from "@/lib/design-system/consumerUi";

interface SubcategoryRulesCardProps {
  subcategoryName: string;
  rule: SubcategoryListingRule;
  /** When provided, show "I have read the rules" checkbox and call onAcknowledgedChange when toggled. */
  acknowledged?: boolean;
  onAcknowledgedChange?: (value: boolean) => void;
}

/**
 * Displays sub-category listing rules in a prominent, on-brand card. Shown on sell/edit when a subcategory with rules is selected.
 */
export default function SubcategoryRulesCard({
  subcategoryName,
  rule,
  acknowledged = false,
  onAcknowledgedChange,
}: SubcategoryRulesCardProps) {
  const title = rule.title?.trim() || `Rules for listing ${subcategoryName}`;
  const body = rule.body?.trim();
  if (!body) return null;

  return (
    <div
      className={`${SIO_BANNER_POSITIVE} mt-4 rounded-lg p-4`}
      role="region"
      aria-labelledby="subcategory-rules-heading"
    >
      <h3 id="subcategory-rules-heading" className="text-sm font-semibold">
        {title}
      </h3>
      <div className="mt-2 whitespace-pre-wrap text-sm">{body}</div>
      {onAcknowledgedChange && (
        <label className="mt-3 flex cursor-pointer items-start gap-2">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => onAcknowledgedChange(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-border text-primary focus:ring-primary"
          />
          <span className="text-sm">
            I have read the rules for listing <b>{subcategoryName}</b> on Sell
            It Off
          </span>
        </label>
      )}
    </div>
  );
}
